/**
 * Configuration des portails de publication.
 *
 * 4 portails à terme :
 *   - website : le site Skyline lui-même (consomme la VENDOR_API). Pilote
 *               uniquement par `biens.statutPublication`. Pas d'API HTTP.
 *   - immoweb : OAuth2 + classified pipeline (cf. immoweb-client.ts)
 *   - spotto  : à venir, en attente de doc API
 *   - zimmo   : à venir, en attente de doc API
 *
 * Phase 3a : seuls `website` et `immoweb` sont activés.
 *
 * `IS_DEMO_MODE` : quand activé, les portails externes sont marqués comme
 * "configurés" et le hook portalPublish bypass l'Edge Function pour utiliser
 * un mock localStorage (cf. portalDemoStore.ts). Permet la démo client sans
 * credentials. À retirer dès que les Edge Functions sont branchées sur la
 * vraie sandbox Immoweb.
 */

// Démo client : true tant que les credentials Immoweb réels ne sont pas reçus.
// Bascule via VITE_IMMOWEB_DEMO_MODE='false' pour tester la vraie chaîne.
export const IS_DEMO_MODE =
  (import.meta.env.VITE_IMMOWEB_DEMO_MODE ?? "true") !== "false";

import type { LucideIcon } from "lucide-react";
import { Globe, Building2, MapPin, Tag } from "lucide-react";

export type PortalId = "website" | "immoweb" | "spotto" | "zimmo";

export interface PortalDefinition {
  id: PortalId;
  /** Libellé affiché à l'utilisateur. Pas de i18n — nom propre. */
  label: string;
  /** Icône Lucide associée — utilisée dans la matrice. */
  icon: LucideIcon;
  /** Activé dans cette phase (visible dans la matrice + modal multi-publish). */
  enabled: boolean;
  /** Configuration credentials OK côté backend. Si false → badge "Non configuré". */
  hasCredentials: boolean;
  /** URL publique de la fiche (pour le bouton "Voir sur le portail"). */
  publicUrl?: (immowebCode: string) => string;
}

export const PORTALS: Record<PortalId, PortalDefinition> = {
  website: {
    id: "website",
    label: "Site Web",
    icon: Globe,
    enabled: true,
    // Le site web ne nécessite pas de credentials externes — il consomme la
    // VENDOR_API en lecture, et `statutPublication = 'Publié'` suffit pour
    // qu'un bien soit visible. Toujours "configuré" par défaut.
    hasCredentials: true,
  },
  immoweb: {
    id: "immoweb",
    label: "Immoweb",
    icon: Building2,
    enabled: true,
    // En mode démo, on marque comme configuré pour permettre les actions
    // (qui sont alors simulées en localStorage). Hors démo, le badge
    // "Non configuré" s'affiche tant que les credentials Immoweb ne sont
    // pas en place côté Edge Function.
    hasCredentials: IS_DEMO_MODE,
    publicUrl: (code) => `https://www.immoweb.be/fr/annonce/${code}`,
  },
  spotto: {
    id: "spotto",
    label: "Spotto",
    icon: MapPin,
    enabled: false, // Phase 3b
    hasCredentials: false,
  },
  zimmo: {
    id: "zimmo",
    label: "Zimmo",
    icon: Tag,
    enabled: false, // Phase 3b
    hasCredentials: false,
  },
};

/** Liste des portails activés (filtre sur `enabled`). */
export function enabledPortals(): PortalDefinition[] {
  return (Object.values(PORTALS) as PortalDefinition[]).filter((p) => p.enabled);
}

/** Liste des portails externes activés (= sans le website, qui se gère via statutPublication). */
export function externalEnabledPortals(): PortalDefinition[] {
  return enabledPortals().filter((p) => p.id !== "website");
}
